/**
 * Dunning efficacy for one ET month, under the renewal calendar and driven by
 * its month: of the renewal invoices that failed and entered dunning that
 * month, how many were won back — by an automatic retry or by the customer
 * paying through the dunning email's link — lost when every retry failed, or
 * cancelled while in dunning. The rate is over resolved invoices; ones still
 * retrying are shown apart. The step table walks the ladder — the failed
 * renewal, then card retries on days 1, 3, 7 and 14 — with what each step
 * recovered and who paid by link or cancelled before the next. Data from /manifold/hs-subscriptions/dunning
 * (services/manifoldSubscriptions dunningEfficacy).
 */

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import { manifoldAPI } from '../../services/api'

const money = cents => (cents ? `$${Math.round(cents / 100).toLocaleString()}` : '$0')
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—')

function Stat({ label, value, sub, className = '' }) {
  return (
    <div className="rounded-md border p-2.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${className}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground tabular-nums">{sub}</div>}
    </div>
  )
}

/** One row per ladder step; a step's link payments and cancellations happened before the next retry. */
function StepTable({ steps }) {
  if (!steps?.length) return null
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs tabular-nums">
        <thead className="text-muted-foreground">
          <tr className="border-b text-left">
            <th className="py-1.5 pr-3 font-medium">Step</th>
            <th className="py-1.5 pr-3 font-medium">Day</th>
            <th className="py-1.5 pr-3 font-medium text-right" title="Invoices charged at this step">Charged</th>
            <th className="py-1.5 pr-3 font-medium text-right" title="Card retry succeeded at this step">Recovered</th>
            <th className="py-1.5 pr-3 font-medium text-right" title="Recovered ÷ charged at this step">Step rate</th>
            <th className="py-1.5 pr-3 font-medium text-right" title="Paid through the dunning email's link before the next retry">Paid via link after</th>
            <th className="py-1.5 font-medium text-right" title="Subscription cancelled before the next retry">Cancelled after</th>
          </tr>
        </thead>
        <tbody>
          {steps.map(step => (
            <tr key={step.step} className="border-b last:border-0">
              <td className="py-1.5 pr-3">{step.step === 0 ? 'Renewal charge' : `Retry ${step.step}`}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">{step.day == null ? '—' : `Day ${step.day}`}</td>
              <td className="py-1.5 pr-3 text-right">{step.charged.toLocaleString()}</td>
              <td className="py-1.5 pr-3 text-right text-green-600">
                {step.step === 0 ? '—' : <>{step.recovered.toLocaleString()}{step.recoveredAmount > 0 && <span className="text-muted-foreground"> · {money(step.recoveredAmount)}</span>}</>}
              </td>
              <td className="py-1.5 pr-3 text-right">{step.step === 0 ? '—' : pct(step.recovered, step.charged)}</td>
              <td className="py-1.5 pr-3 text-right">
                {step.paidViaLink.toLocaleString()}{step.paidViaLinkAmount > 0 && <span className="text-muted-foreground"> · {money(step.paidViaLinkAmount)}</span>}
              </td>
              <td className="py-1.5 text-right">{step.cancelled.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function DunningEfficacy({ month }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    manifoldAPI.getDunningEfficacy({ month })
      .then(response => { if (alive && response.data.success) setData(response.data.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [month])

  const shown = data && data.month === month ? data : null

  return (
    <div className="mt-6 border-t pt-4">
      <div className="mb-3">
        <h3 className="text-sm font-medium">Dunning</h3>
        <p className="text-xs text-muted-foreground">
          Renewals that failed and went into dunning this month, and how they ended. Recovery rate counts only those that have finished; ones still retrying are shown apart.
        </p>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && !shown && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
      {shown && !shown.entered.count && <p className="text-sm text-muted-foreground">No renewals went into dunning this month.</p>}
      {shown && shown.entered.count > 0 && (
        <div className={loading ? 'opacity-60' : ''}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <Stat label="Entered dunning" value={shown.entered.count.toLocaleString()} sub={money(shown.entered.amount)} />
            <Stat
              label="Recovery rate"
              value={shown.recoveryRate == null ? '—' : `${Math.round(shown.recoveryRate * 100)}%`}
              sub={`${shown.recovered.count} of ${shown.resolved} finished`}
            />
            <Stat
              label="Recovered"
              value={shown.recovered.count.toLocaleString()}
              sub={`${money(shown.recovered.amount)} · ${shown.recoveredByRetry.count} retry, ${shown.recoveredByLink.count} via link`}
              className="text-green-600"
            />
            <Stat label="Lost (retries used up)" value={shown.exhausted.count.toLocaleString()} sub={money(shown.exhausted.amount)} className={shown.exhausted.count ? 'text-destructive' : ''} />
            <Stat label="Cancelled in dunning" value={shown.cancelled.count.toLocaleString()} sub={money(shown.cancelled.amount)} />
            <Stat label="Still retrying" value={shown.inProgress.count.toLocaleString()} sub={money(shown.inProgress.amount)} />
          </div>
          {shown.medianDaysToRecover != null && (
            <p className="mt-3 text-xs text-muted-foreground tabular-nums">
              Median time to recover: <span className="font-medium text-foreground">{shown.medianDaysToRecover < 1 ? `${Math.round(shown.medianDaysToRecover * 24)} hours` : `${shown.medianDaysToRecover.toFixed(1)} days`}</span>
            </p>
          )}
          <StepTable steps={shown.steps} />
        </div>
      )}
    </div>
  )
}
